import { FgdsProductType } from "@domain/product/product";
import { AlarmDeviceConfiguration } from "@domain/project/configurations/alarm-device-configuration";
import { GasWarningCenterConfiguration } from "@domain/project/configurations/gas-warning-center-configuration";
import { PlasticSignConfiguration } from "@domain/project/configurations/plastic-sign-configuration";
import { SignalElementConfiguration } from "@domain/project/configurations/signal-element-configuration";
import { TransmitterConfiguration } from "@domain/project/configurations/transmitter-configuration";
import { FloorplanAlarmDevice } from "@domain/project/floorplan/floorplan-alarm-device";
import { FloorplanFlameDetector } from "@domain/project/floorplan/floorplan-flame-detector";
import { FloorplanFlameDetectorPlaceholder } from "@domain/project/floorplan/floorplan-flame-detector-placeholder";
import { FloorplanGasWarningCenter } from "@domain/project/floorplan/floorplan-gas-warning-center";
import { FloorplanGasWarningCenterPlaceholder } from "@domain/project/floorplan/floorplan-gas-warning-center-placeholder";
import { FloorplanPlaceholder } from "@domain/project/floorplan/floorplan-placeholder";
import { FloorplanPlasticSign } from "@domain/project/floorplan/floorplan-plastic-sign";
import { FloorplanSignalElement } from "@domain/project/floorplan/floorplan-signal-element";
import { FloorplanSimplePlaceholder } from "@domain/project/floorplan/floorplan-simple-placeholder";
import { FloorplanTransmitter } from "@domain/project/floorplan/floorplan-transmitter";
import { FloorplanTransmitterPlaceholder } from "@domain/project/floorplan/floorplan-transmitter-placeholder";
import { FloorplanPatientMonitor } from "@domain/project/floorplan/monitoring/floorplan-patient-monitor";
import { ProductData } from "@domain/project/product-data/product-data";
import { ConfigProductMap } from "@domain/project/product-list/config-product-map";
import { ProductSection } from "@domain/project/product-list/product-section";
import { ALPHABET } from "@utils/alphabet";
import { FlameDetectorConfiguration } from "../configurations/flame-detector-configuration";

export class GroupSection {
  name: string = "";
  floorplanName: string = "";
  subTotal: number = 0;
  zrefSubTotal: number = 0;
  alarmDevices!: ProductSection<FloorplanAlarmDevice, FloorplanSimplePlaceholder, AlarmDeviceConfiguration, ConfigProductMap>;
  gasWarningCenters!: ProductSection<
    FloorplanGasWarningCenter,
    FloorplanGasWarningCenterPlaceholder,
    GasWarningCenterConfiguration,
    ConfigProductMap
  >;
  plasticSigns!: ProductSection<FloorplanPlasticSign, FloorplanSimplePlaceholder, PlasticSignConfiguration, ConfigProductMap>;
  signalElements!: ProductSection<
    FloorplanSignalElement,
    FloorplanSimplePlaceholder,
    SignalElementConfiguration,
    ConfigProductMap
  >;
  transmitters!: ProductSection<
    FloorplanTransmitter,
    FloorplanTransmitterPlaceholder,
    TransmitterConfiguration,
    ConfigProductMap
  >;
  flameDetectors!: ProductSection<
    FloorplanFlameDetector,
    FloorplanFlameDetectorPlaceholder,
    FlameDetectorConfiguration,
    ConfigProductMap
  >;
  constructor(name: string, floorplanName?: string) {
    this.name = name;
    this.floorplanName = floorplanName ?? "";
  }

  createAlarmDevices(devices: FloorplanAlarmDevice[], placeholders: FloorplanSimplePlaceholder[], mappings: ConfigProductMap[]) {
    placeholders.forEach((placeholder) => {
      this.setPositionIdForPlaceholderProduct(placeholder);
    });

    devices.forEach((alarmDevice) => {
      const config = mappings.find((mapping) => alarmDevice.config.id === mapping.config.id);
      if (alarmDevice.products) this.setPositionIdForCustomProducts(alarmDevice, config);
    });

    this.alarmDevices = new ProductSection(devices, placeholders, mappings, FgdsProductType.ALARMDEVICE);
  }

  createGasWarningCenters(
    devices: FloorplanGasWarningCenter[],
    placeholders: FloorplanGasWarningCenterPlaceholder[],
    mappings: ConfigProductMap[],
  ) {
    placeholders.forEach((placeholder) => {
      this.setPositionIdForPlaceholderProduct(placeholder);
    });

    devices.forEach((gasWarningCenter) => {
      const config = mappings.find((mapping) => gasWarningCenter.config.id === mapping.config.id);

      if (gasWarningCenter.products) this.setPositionIdForCustomProducts(gasWarningCenter, config);
    });

    this.gasWarningCenters = new ProductSection(devices, placeholders, mappings, FgdsProductType.GASWARNINGCENTER);
  }

  createPlasticSigns(devices: FloorplanPlasticSign[], placeholders: FloorplanSimplePlaceholder[], mappings: ConfigProductMap[]) {
    placeholders.forEach((placeholder) => {
      this.setPositionIdForPlaceholderProduct(placeholder);
    });
    devices.forEach((plasticSign) => {
      const config = mappings.find((mapping) => plasticSign.config.id === mapping.config.id);
      if (plasticSign.products) this.setPositionIdForCustomProducts(plasticSign, config);
    });
    this.plasticSigns = new ProductSection(devices, placeholders, mappings, FgdsProductType.PLASTICSIGN);
  }

  createSignalElements(
    devices: FloorplanSignalElement[],
    placeholders: FloorplanSimplePlaceholder[],
    mappings: ConfigProductMap[],
  ) {
    placeholders.forEach((placeholder) => {
      this.setPositionIdForPlaceholderProduct(placeholder);
    });
    devices.forEach((signalElement) => {
      const config = mappings.find((mapping) => signalElement.config.id === mapping.config.id);
      if (signalElement.products) this.setPositionIdForCustomProducts(signalElement, config);
    });
    this.signalElements = new ProductSection(devices, placeholders, mappings, FgdsProductType.SIGNALELEMENT);
  }

  createTransmitters(
    devices: FloorplanTransmitter[],
    placeholders: FloorplanTransmitterPlaceholder[],
    mappings: ConfigProductMap[],
  ) {
    placeholders.forEach((placeholder) => {
      this.setPositionIdForPlaceholderProduct(placeholder);
    });
    devices.forEach((transmitter) => {
      const config = mappings.find((mapping) => transmitter.config.id === mapping.config.id);
      if (transmitter.products) this.setPositionIdForCustomProducts(transmitter, config);
    });
    this.transmitters = new ProductSection(devices, placeholders, mappings, FgdsProductType.TRANSMITTER);
  }

  createFlameDetectors(
    devices: FloorplanFlameDetector[],
    placeholders: FloorplanFlameDetectorPlaceholder[],
    mappings: ConfigProductMap[],
  ) {
    placeholders.forEach((placeholder) => {
      this.setPositionIdForPlaceholderProduct(placeholder);
    });
    devices.forEach((flameDetector) => {
      const config = mappings.find((mapping) => flameDetector.config.id === mapping.config.id);
      if (flameDetector.products) this.setPositionIdForCustomProducts(flameDetector, config);
    });
    this.flameDetectors = new ProductSection(devices, placeholders, mappings, FgdsProductType.FLAME_DETECTOR);
  }

  calculateSubTotal() {
    const prices = [
      this.alarmDevices.subtotal,
      this.gasWarningCenters.subtotal,
      this.plasticSigns.subtotal,
      this.signalElements.subtotal,
      this.transmitters.subtotal,
      this.flameDetectors.subtotal,
    ];
    this.subTotal = prices.reduce((total, price) => total + price, 0);
  }

  calculateZrefSubTotal() {
    const prices = [
      this.alarmDevices.zrefSubTotal ?? 0,
      this.gasWarningCenters.zrefSubTotal ?? 0,
      this.plasticSigns.zrefSubTotal ?? 0,
      this.signalElements.zrefSubTotal ?? 0,
      this.transmitters.zrefSubTotal ?? 0,
      this.flameDetectors.zrefSubTotal ?? 0,
    ];
    this.zrefSubTotal = prices.reduce((total, price) => total + price, 0);
  }

  getAttachmentInstructionsForGroup(): ProductData[] {
    const attachmentInstructions: ProductData[] = [];
    attachmentInstructions.push(...this.collectAttachmentInstructionsFromProductSection(this.alarmDevices));
    attachmentInstructions.push(...this.collectAttachmentInstructionsFromProductSection(this.gasWarningCenters));
    attachmentInstructions.push(...this.collectAttachmentInstructionsFromProductSection(this.plasticSigns));
    attachmentInstructions.push(...this.collectAttachmentInstructionsFromProductSection(this.signalElements));
    attachmentInstructions.push(...this.collectAttachmentInstructionsFromProductSection(this.transmitters));
    attachmentInstructions.push(...this.collectAttachmentInstructionsFromProductSection(this.flameDetectors));
    return attachmentInstructions;
  }

  private collectAttachmentInstructionsFromProductSection(
    productSection: ProductSection<
      | FloorplanAlarmDevice
      | FloorplanGasWarningCenter
      | FloorplanPlasticSign
      | FloorplanSignalElement
      | FloorplanTransmitter
      | FloorplanFlameDetector,
      FloorplanPlaceholder,
      | AlarmDeviceConfiguration
      | GasWarningCenterConfiguration
      | PlasticSignConfiguration
      | SignalElementConfiguration
      | TransmitterConfiguration
      | FlameDetectorConfiguration,
      ConfigProductMap
    >,
  ) {
    const attachmentInstructions: ProductData[] = [];
    productSection.mappings.map((mapping) => {
      if (mapping.attachmentInstructions) attachmentInstructions.push(...mapping.attachmentInstructions);
    });
    return attachmentInstructions;
  }

  private setPositionIdForPlaceholderProduct(
    placeholder:
      | FloorplanSimplePlaceholder
      | FloorplanGasWarningCenterPlaceholder
      | FloorplanTransmitterPlaceholder
      | FloorplanFlameDetectorPlaceholder
      | FloorplanGasWarningCenter,
  ) {
    if (placeholder.products.length > 0) {
      placeholder.products.forEach((product, index) => {
        if (product.id) {
          product.positionId = `${placeholder.positionId?.split(".")[0]}.${ALPHABET[index]}`;
        }
      });
    }
  }

  private setPositionIdForCustomProducts(
    product:
      | FloorplanGasWarningCenter
      | FloorplanAlarmDevice
      | FloorplanPlasticSign
      | FloorplanSignalElement
      | FloorplanTransmitter
      | FloorplanFlameDetector
      | FloorplanPatientMonitor,
    config?: ConfigProductMap,
  ) {
    if (product.products.length > 0) {
      const lastPositionId = config?.data[config.data.length - 1]?.positionId;
      if (!lastPositionId) return;

      const [baseId, lastLetter] = lastPositionId.split(".");
      const startIndex = Math.max(ALPHABET.indexOf(lastLetter) + 1, 0);
      product.products.forEach((p, index) => {
        if (p.id) {
          p.positionId = `${baseId}.${ALPHABET[startIndex + index]}`;
        }
      });
    }
  }
}
